import { ApiProjectSettingKey, ApiProjectType } from "@incendium/api";
import {
  Alert,
  Autocomplete,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { StyledDrawerActions } from "Components/UI/StyledDrawer";
import { AccordianChartBuilderSidebarBlock } from "features/chartLibrary";
import { UtmMappingList } from "features/utms";
import { enumToArray, formatEnumVal } from "Helpers/enumToText";
import { TProjectSettings } from "Hooks";
import { useUTMs } from "Hooks/useUTMs";
import produce from "immer";
import { CallbackOrVal } from "Interfaces";
import { useCallback } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";

interface IProjectOptionsDrawerFormProps {
  onSubmit: (e: React.FormEvent) => Promise<void>;
  onClose?: () => void;
  projectSettings: TProjectSettings;
  setProjectSettings: (v: CallbackOrVal<TProjectSettings>) => void;
  showSettings?: [ApiProjectSettingKey];
  hideUTMS?: boolean;
  saveText?: string;
}

function ProjectOptionsDrawerForm({
  onSubmit,
  onClose,
  projectSettings,
  setProjectSettings,
  showSettings,
  hideUTMS,
  saveText,
}: IProjectOptionsDrawerFormProps) {
  const { utms, setUTMs } = useUTMs();

  const handleChange = useCallback(
    (key: ApiProjectSettingKey, value: string | null) => {
      setProjectSettings((settings) =>
        produce(settings, (draft) => {
          if (!value) {
            draft[key] = undefined;
          } else {
            draft[key] = value;
          }
        }),
      );
    },
    [setProjectSettings],
  );

  const shouldShow = useCallback(
    (settings: ApiProjectSettingKey[]) =>
      !showSettings || showSettings.some((s) => settings.includes(s)),
    [showSettings],
  );

  return (
    <ValidatorForm onSubmit={onSubmit}>
      {shouldShow([ApiProjectSettingKey.PROJECT_TYPE]) && (
        <AccordianChartBuilderSidebarBlock
          title="Project Type"
          subTitle="Configure your project's type(s). This affects analytics, tagging options, and available features."
        >
          <Stack spacing={3}>
            <Autocomplete
              multiple
              options={enumToArray(ApiProjectType).filter(
                (s) => s !== ApiProjectType.TYPE_UNSPECIFIED,
              )}
              getOptionLabel={(o) => formatEnumVal(o.replace("Type", ""))}
              value={
                (projectSettings[ApiProjectSettingKey.PROJECT_TYPE]
                  ? (
                      projectSettings[
                        ApiProjectSettingKey.PROJECT_TYPE
                      ] as string
                    )
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                  : []) as any
              }
              onChange={(e, v) => {
                const joined = (v || []).join(",");
                handleChange(
                  ApiProjectSettingKey.PROJECT_TYPE,
                  joined.length ? joined : null,
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project Type"
                  helperText="Select one or more types that best describe your project (e.g., Ecommerce, B2B, Recruitment)."
                />
              )}
            />
          </Stack>
        </AccordianChartBuilderSidebarBlock>
      )}
      {shouldShow([
        ApiProjectSettingKey.BLOCKED_IPS,
        ApiProjectSettingKey.IGNORE_REFERRERS,
        ApiProjectSettingKey.PAGE_USE_QUERY_STRING,
      ]) && (
        <AccordianChartBuilderSidebarBlock
          title="Data Capture"
          subTitle="These settings affect the way we capture data."
        >
          <Stack spacing={3}>
            {shouldShow([ApiProjectSettingKey.BLOCKED_IPS]) && (
              <TextValidator
                variant="outlined"
                size="small"
                fullWidth
                label="Blocked IP's"
                helperText="Comma seperated list of ip's to block"
                name={ApiProjectSettingKey.BLOCKED_IPS}
                value={projectSettings[ApiProjectSettingKey.BLOCKED_IPS] || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange(ApiProjectSettingKey.BLOCKED_IPS, e.target.value)
                }
              />
            )}
            {shouldShow([ApiProjectSettingKey.IGNORE_REFERRERS]) && (
              <Autocomplete
                multiple
                freeSolo
                size="small"
                fullWidth
                options={[]}
                value={
                  projectSettings[ApiProjectSettingKey.IGNORE_REFERRERS]
                    ? (
                        projectSettings[
                          ApiProjectSettingKey.IGNORE_REFERRERS
                        ] as string
                      )
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean)
                    : []
                }
                onChange={(_, v) => {
                  const joined = (v || []).join(",").trim();
                  handleChange(
                    ApiProjectSettingKey.IGNORE_REFERRERS,
                    joined.length ? joined : null,
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Referrers to ignore"
                    helperText="Add referrer domains to ignore as a new session (e.g. google.com)"
                  />
                )}
              />
            )}
            {shouldShow([ApiProjectSettingKey.PAGE_USE_QUERY_STRING]) && (
              <Stack>
                <FormControlLabel
                  componentsProps={{
                    typography: {
                      variant: "body1",
                    },
                  }}
                  control={
                    <Switch
                      checked={
                        projectSettings[
                          ApiProjectSettingKey.PAGE_USE_QUERY_STRING
                        ] === "true"
                      }
                      onChange={(e, checked) => {
                        handleChange(
                          ApiProjectSettingKey.PAGE_USE_QUERY_STRING,
                          checked ? "true" : "false",
                        );
                      }}
                    />
                  }
                  label="Capture Query Parameters"
                />
                <Typography variant="body2">
                  Query parameters are URL components used for passing data as
                  key-value pairs to a web server or application. If you do not
                  use query parameters to do thing such as change a product on a
                  page or change titles etc then you can turn this feature off.
                  Turning this feature off will improve crawling speeds and give
                  more accurate results.
                </Typography>
              </Stack>
            )}
            {shouldShow([ApiProjectSettingKey.DISABLE_CRAWLER]) && (
              <Stack>
                <FormControlLabel
                  componentsProps={{
                    typography: {
                      variant: "body1",
                    },
                  }}
                  control={
                    <Switch
                      checked={
                        projectSettings[
                          ApiProjectSettingKey.DISABLE_CRAWLER
                        ] === "true"
                      }
                      onChange={(e, checked) => {
                        handleChange(
                          ApiProjectSettingKey.DISABLE_CRAWLER,
                          checked ? "true" : "false",
                        );
                      }}
                    />
                  }
                  label="Disable Crawler"
                />
                {projectSettings[ApiProjectSettingKey.DISABLE_CRAWLER] ===
                "true" ? (
                  <Alert severity="warning">
                    Crawler Disabled. Advanced features such as product page
                    analytics and location page insights are unavailable while
                    the crawler is turned off.
                  </Alert>
                ) : (
                  <Typography variant="body2">
                    This is an advanced setting. Disabling the crawler will
                    prevent us from scanning your site, which will deactivate
                    advanced features like product page analytics and location
                    page insights.
                  </Typography>
                )}
              </Stack>
            )}
            {shouldShow([
              ApiProjectSettingKey.ENABLE_CRAWLER_PAGE_STATUS_CODE,
            ]) && (
              <Stack>
                <FormControlLabel
                  componentsProps={{
                    typography: {
                      variant: "body1",
                    },
                  }}
                  control={
                    <Switch
                      checked={
                        projectSettings[
                          ApiProjectSettingKey.ENABLE_CRAWLER_PAGE_STATUS_CODE
                        ] === "true"
                      }
                      onChange={(e, checked) => {
                        handleChange(
                          ApiProjectSettingKey.ENABLE_CRAWLER_PAGE_STATUS_CODE,
                          checked ? "true" : "false",
                        );
                      }}
                    />
                  }
                  label="Enable HTTP Status Crawler"
                />
                <Typography variant="body2">
                  This is an advanced setting. When enabled, this feature will
                  periodically crawl all pages visited on your website in the
                  past month to check their HTTP status codes (e.g., 200, 404).
                  This data will be reflected in your analytics. Note: While we
                  crawl in an intelligent way to reduce impact, activating this
                  setting may increase load on your system due to the additional
                  activity.
                </Typography>
              </Stack>
            )}
          </Stack>
        </AccordianChartBuilderSidebarBlock>
      )}
      {shouldShow([ApiProjectSettingKey.FB_PIXEL]) && (
        <AccordianChartBuilderSidebarBlock
          title="Facebook / Meta Pixel"
          subTitle="Add your pixel number, this will unlock options such as pushing custom data back to your meta ads account"
        >
          <TextValidator
            variant="outlined"
            size="small"
            fullWidth
            label="Pixel Id"
            helperText="Copy from Events Manager"
            name={ApiProjectSettingKey.FB_PIXEL}
            value={projectSettings[ApiProjectSettingKey.FB_PIXEL] || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(ApiProjectSettingKey.FB_PIXEL, e.target.value)
            }
          />
        </AccordianChartBuilderSidebarBlock>
      )}
      {shouldShow([
        ApiProjectSettingKey.RETENTION_MAX_CHANCE,
        ApiProjectSettingKey.RETENTION_LIFETIME_MONTHS,
        ApiProjectSettingKey.RETENTION_GRACE_PERIOD_MONTHS,
      ]) && (
        <AccordianChartBuilderSidebarBlock
          title="Retention"
          subTitle=" Our machine learning model predicts the likelihood of a user making
    a repeat purchase, and we categorize a lead as 'at risk' if their
    chance of converting falls below a certain percentage (max chance),
    and if their last purchase was not made within the past (grace
    period) months, and it has been (lifetime) months since their last
    purchase. These settings enable you to customize this prediction and
    categorization process."
        >
          <Stack spacing={3}>
            {shouldShow([ApiProjectSettingKey.RETENTION_MAX_CHANCE]) && (
              <TextValidator
                variant="outlined"
                size="small"
                fullWidth
                label="Max Chance"
                type="number"
                min={0}
                max={100}
                validators={["minNumber:0", "maxNumber:100"]}
                errorMessages={[
                  "Max Chance must be between 0 and 100",
                  "Max Chance must be between 0 and 100",
                ]}
                helperText="The maximum chance of conversion for a lead before they are considered `at risk`"
                name={ApiProjectSettingKey.RETENTION_MAX_CHANCE}
                value={
                  projectSettings[ApiProjectSettingKey.RETENTION_MAX_CHANCE] ||
                  ""
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange(
                    ApiProjectSettingKey.RETENTION_MAX_CHANCE,
                    e.target.value,
                  )
                }
              />
            )}
            {shouldShow([ApiProjectSettingKey.RETENTION_LIFETIME_MONTHS]) && (
              <TextValidator
                variant="outlined"
                size="small"
                fullWidth
                label="Lifetime"
                type="number"
                min={1}
                validators={["minNumber:1"]}
                errorMessages={["Lifetime must be a minimum of 1 month"]}
                helperText="The Lifetime of a lead is determined by the number of months elapsed since their last visit, after which they are considered inactive or 'dead'."
                name={ApiProjectSettingKey.RETENTION_LIFETIME_MONTHS}
                value={
                  projectSettings[
                    ApiProjectSettingKey.RETENTION_LIFETIME_MONTHS
                  ] || ""
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange(
                    ApiProjectSettingKey.RETENTION_LIFETIME_MONTHS,
                    e.target.value,
                  )
                }
              />
            )}
            {shouldShow([
              ApiProjectSettingKey.RETENTION_GRACE_PERIOD_MONTHS,
            ]) && (
              <TextValidator
                variant="outlined"
                size="small"
                fullWidth
                label="Grace Period"
                type="number"
                min={0}
                validators={["minNumber:0"]}
                errorMessages={["Grace period must be a minimum of 0 months"]}
                helperText="The Grace period refers to the number of months since a lead's last transaction that we overlook their inactivity before considering them at risk."
                name={ApiProjectSettingKey.RETENTION_GRACE_PERIOD_MONTHS}
                value={
                  projectSettings[
                    ApiProjectSettingKey.RETENTION_GRACE_PERIOD_MONTHS
                  ] || ""
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange(
                    ApiProjectSettingKey.RETENTION_GRACE_PERIOD_MONTHS,
                    e.target.value,
                  )
                }
              />
            )}
          </Stack>
        </AccordianChartBuilderSidebarBlock>
      )}
      {shouldShow([
        ApiProjectSettingKey.SHOW_AWARENESS_ACQUISTION_RETENTION_INSIGHTS,
      ]) && (
        <AccordianChartBuilderSidebarBlock
          title="Analyse"
          subTitle={
            <>
              <Typography variant="body2">
                These settings enable customization of your analysis section.
              </Typography>
              <Typography variant="body2">
                Important: You must select either{" "}
                {formatEnumVal(
                  ApiProjectType.TYPE_ECOMMERCE.replace("Type", ""),
                )}{" "}
                or{" "}
                {formatEnumVal(
                  ApiProjectType.TYPE_RECRUITMENT.replace("Type", ""),
                )}{" "}
                if you wish to use impressions.
              </Typography>
            </>
          }
        >
          <Stack spacing={3}>
            <Stack>
              <FormControlLabel
                componentsProps={{
                  typography: {
                    variant: "body1",
                  },
                }}
                control={
                  <Switch
                    checked={
                      projectSettings[
                        ApiProjectSettingKey
                          .SHOW_AWARENESS_ACQUISTION_RETENTION_INSIGHTS
                      ] === "true"
                    }
                    onChange={(e, checked) => {
                      handleChange(
                        ApiProjectSettingKey.SHOW_AWARENESS_ACQUISTION_RETENTION_INSIGHTS,
                        checked ? "true" : "false",
                      );
                    }}
                  />
                }
                label="Show Awareness, Aquisition and Retention insights block in reports"
              />
              <Typography variant="body2">
                This block shows insights in these funnel stages based on
                configuration of Reserved Traffic Buckets.
              </Typography>
            </Stack>
          </Stack>
        </AccordianChartBuilderSidebarBlock>
      )}
      {!hideUTMS && (
        <AccordianChartBuilderSidebarBlock
          title="Custom UTM Mapping"
          subTitle="Customize UTM mappings by overriding default settings. This can be done on a per-publisher basis or by creating one or multiple catch-all configurations. Our system will search through each mapping until the correct one is found."
        >
          <UtmMappingList utms={utms} setUTMs={setUTMs} showProvider />
        </AccordianChartBuilderSidebarBlock>
      )}
      <StyledDrawerActions justifyContent={"flex-end"}>
        {onClose && (
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
        )}
        <Button variant="contained" color="primary" type="submit">
          {saveText || "Save"}
        </Button>
      </StyledDrawerActions>
    </ValidatorForm>
  );
}

export default ProjectOptionsDrawerForm;
